import { ScrollReveal } from './ScrollReveal';
import { Globe, MapPin } from 'lucide-react';

const LANGUAGES = [
  { code: 'EN', name: 'English', native: 'English' },
  { code: 'NL', name: 'Dutch', native: 'Nederlands' },
  { code: 'FR', name: 'French', native: 'Francais' },
  { code: 'DE', name: 'German', native: 'Deutsch' },
  { code: 'ES', name: 'Spanish', native: 'Espanol' },
  { code: 'IT', name: 'Italian', native: 'Italiano' },
  { code: 'PT', name: 'Portuguese', native: 'Portugues' },
  { code: 'AR', name: 'Arabic', native: 'العربية' },
  { code: 'ZH', name: 'Chinese', native: '中文' },
  { code: 'JA', name: 'Japanese', native: '日本語' },
  { code: 'KO', name: 'Korean', native: '한국어' },
  { code: 'RU', name: 'Russian', native: 'Русский' },
  { code: 'TR', name: 'Turkish', native: 'Turkce' },
  { code: 'HI', name: 'Hindi', native: 'हिन्दी' },
  { code: 'PL', name: 'Polish', native: 'Polski' },
  { code: 'SV', name: 'Swedish', native: 'Svenska' },
];

const REGIONS = [
  { name: 'Europe', cities: ['London', 'Amsterdam', 'Paris', 'Frankfurt', 'Zurich', 'Milan', 'Madrid', 'Stockholm'] },
  { name: 'Middle East', cities: ['Dubai', 'Riyadh', 'Doha', 'Abu Dhabi'] },
  { name: 'Asia Pacific', cities: ['Singapore', 'Hong Kong', 'Tokyo', 'Sydney'] },
  { name: 'Americas', cities: ['New York', 'Miami', 'Toronto', 'Sao Paulo'] },
  { name: 'Africa', cities: ['Johannesburg', 'Lagos', 'Nairobi'] },
];

export default function GlobalPresence() {
  return (
    <section className="relative py-24 sm:py-32 overflow-hidden" data-testid="global-presence-section">
      <div className="absolute inset-0 grid-bg pointer-events-none opacity-20" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-aurocle-gold/3 rounded-full blur-[250px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <div className="overline mb-4">Global Operations</div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-aurocle-text mb-4">
              Worldwide. Multilingual. Enterprise-ready.
            </h2>
            <p className="text-base text-aurocle-platinum-muted max-w-2xl mx-auto">
              Aurocle Agents operates across borders, languages, and regulatory frameworks. Your private workforce understands your business — in any language, in any market.
            </p>
          </div>
        </ScrollReveal>

        {/* Languages */}
        <ScrollReveal delay={100}>
          <div className="mb-16">
            <div className="flex items-center justify-center gap-2 mb-8">
              <Globe size={16} className="text-aurocle-gold" />
              <span className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60">Supported Languages</span>
            </div>
            <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
              {LANGUAGES.map((lang, i) => (
                <div key={lang.code}
                  className="group px-4 py-2.5 bg-aurocle-surface border border-white/5 hover:border-aurocle-gold/20 transition-all cursor-default"
                  style={{ animationDelay: `${i * 40}ms` }}
                  data-testid={`lang-${lang.code.toLowerCase()}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-aurocle-gold/50 w-5">{lang.code}</span>
                    <span className="text-xs text-aurocle-text group-hover:text-aurocle-gold transition-colors">{lang.name}</span>
                    <span className="text-[10px] text-aurocle-text-secondary hidden sm:inline">({lang.native})</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-xs text-aurocle-text-secondary mt-4">
              + additional languages available on request for enterprise deployments
            </p>
          </div>
        </ScrollReveal>

        {/* Regions */}
        <ScrollReveal delay={200}>
          <div className="mb-12">
            <div className="flex items-center justify-center gap-2 mb-8">
              <MapPin size={16} className="text-aurocle-gold" />
              <span className="text-xs font-mono uppercase tracking-wider text-aurocle-gold/60">Active Regions</span>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {REGIONS.map((region, i) => (
                <ScrollReveal key={region.name} delay={i * 60}>
                  <div className="premium-card p-5 h-full" data-testid={`region-${region.name.toLowerCase().replace(/\s/g, '-')}`}>
                    <div className="relative z-10">
                      <div className="text-xs font-mono text-aurocle-gold uppercase tracking-wider mb-3">{region.name}</div>
                      <div className="space-y-1.5">
                        {region.cities.map((city) => (
                          <div key={city} className="flex items-center gap-2 text-xs text-aurocle-platinum-muted">
                            <div className="w-1 h-1 rounded-full bg-aurocle-gold/40 shrink-0" />
                            {city}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* Global strip */}
        <ScrollReveal delay={300}>
          <div className="glass-card p-6 border-aurocle-gold/10 text-center">
            <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-xs font-mono uppercase tracking-wider text-aurocle-text-secondary">
              {['Multi-country', 'Multi-entity', 'Multi-language', 'Multi-timezone', 'Multi-currency', 'Cross-border', 'Local compliance'].map((item) => (
                <span key={item} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-aurocle-gold/50" />{item}
                </span>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
